import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, tap } from 'rxjs/operators';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import {
  AUDIT_FACET_FILTERS_EVENTS,
  buildAuditListQueryParams,
  mapAuditLogRowToDisplay,
  normalizeAuditListResponse,
} from './audit-log-datasource.utils';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value.trim());
}

function findEventGuidFromUrl(url: string): string | undefined {
  const pathOnly = url.split('?')[0] || '';
  const pathSegments = pathOnly.split('/').filter(Boolean);

  // Prefer UUID that appears directly after event-specific route segments
  const eventSegmentIndex = pathSegments.findIndex((segment) => {
    const normalized = segment.toLowerCase();
    return normalized === 'event' || normalized === 'events' || normalized === 'begivenhed';
  });

  if (eventSegmentIndex >= 0 && pathSegments[eventSegmentIndex + 1]) {
    const candidate = pathSegments[eventSegmentIndex + 1];
    if (isUuid(candidate)) {
      return candidate;
    }
  }

  // Fallback: if URL contains multiple UUIDs, the event UUID is typically the last one
  const allUuids = pathSegments.filter((segment) => isUuid(segment));
  return allUuids.length ? allUuids[allUuids.length - 1] : undefined;
}

function resolveEventGuid(params: LoadDataParams | undefined, url: string): string | undefined {
  const filterColumns = ['eventGuid', 'eventUuid', 'event_guid', 'guid', 'uuid'];
  const fromFilter = params?.filters?.find(
    (f) => filterColumns.includes(String(f.column)) && isUuid(f.value)
  )?.value as string | undefined;
  if (fromFilter) {
    return fromFilter;
  }

  const fromQuery = params?.queryParams
    ? (Object.entries(params.queryParams).find(
        ([key, value]) => filterColumns.includes(String(key)) && isUuid(value)
      )?.[1] as string | undefined)
    : undefined;
  if (fromQuery) {
    return fromQuery;
  }

  return findEventGuidFromUrl(url);
}

export function audit_logs_events(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router
) {
    return {
      name: 'Event Audit Logs',
      description: 'Audit logs for event changes and participant updates',
      execute: (params?: LoadDataParams) => {

        const eventGuid = resolveEventGuid(params, router.url);

        // Build paging request
        const request = componentDataSourceService['convertToListRequest']({
          ...params,
          dataSourceKey: 'audit_logs_events'
        }, 'audit_logs_events');
        request.includeFacets = true;

        // If we cannot determine the event GUID, return an empty set
        if (!eventGuid) {
          console.warn('[audit_logs_events] No valid event GUID found in URL');
          return of({ items: [], totalCount: 0, pageSize: request.pageSize, pageIndex: (request.page || 1) - 1 });
        }

        // Call the event-specific audit log endpoint
        const endpoint = `${componentDataSourceService.apiUrl}/audit-log/event/${eventGuid}`;
        const queryParams = buildAuditListQueryParams(request);

        return http.get<any>(endpoint, { params: queryParams }).pipe(
          catchError(error => {
            console.error('Error fetching event audit logs:', error);
            return of({ items: [], totalCount: 0, pageSize: request.pageSize, pageIndex: request.page - 1 });
          }),
          map((result: any) => {
            const norm = normalizeAuditListResponse(result, request.pageSize);
            if (norm.items.length) {
              try {
                const auditLogs = norm.items.map((log: any) => ({
                  ...mapAuditLogRowToDisplay(log),
                  event_guid: eventGuid,
                }));

                return {
                  items: auditLogs,
                  totalCount: norm.totalCount,
                  pageSize: norm.pageSize,
                  pageIndex: norm.pageIndex,
                  facets: norm.facets ?? result?.facets,
                };
              } catch (error) {
                console.error('Error processing event audit logs:', error);
                return { items: [], totalCount: 0, pageSize: norm.pageSize, pageIndex: norm.pageIndex };
              }
            } else {
              return {
                items: [],
                totalCount: norm.totalCount,
                pageSize: norm.pageSize,
                pageIndex: norm.pageIndex,
                facets: norm.facets ?? result?.facets,
              };
            }
          })
        );
      },
      settings: {
        columns: [
          { 
            key: 'changed_at', 
            header: 'Hvornår', 
            type: 'text',
            transform: (value: string) => {
              if (!value) return 'N/A';
              return new Date(value).toLocaleString('da-DK');
            },
            sortable: true,
            width: '180px' // Increased width
          },
          { 
            key: 'changed_by_name', 
            header: 'Hvem (udført af)', 
            type: 'text',
            sortable: true,
            width: '170px'
          },
          {
            key: 'affected_user_name',
            header: 'Berørt',
            type: 'text',
            transform: (name: string, row: any) => {
              if (!name || name === '-') return '-';
              if (row.affected_user_email) {
                return `${name} (${row.affected_user_email})`;
              }
              return name;
            },
            sortable: true,
            width: '220px'
          },
          { 
            key: 'what_label', 
            header: 'Hvad', 
            type: 'text',
            sortable: true,
            width: '130px'
          },
          { 
            key: 'field_label', 
            header: 'Ændring', 
            type: 'text',
            sortable: true,
            width: '200px'
          },
          { 
            key: 'change_type_label', 
            header: 'Handling', 
            type: 'text',
            sortable: true,
            width: '110px'
          },
          { 
            key: 'value_from_display', 
            header: 'Fra', 
            type: 'text',
            transform: (value: string) => {
              if (!value) return '-';
              if (value.length > 120) return value.substring(0, 120) + '…';
              return value;
            },
            width: '220px'
          },
          { 
            key: 'value_to_display', 
            header: 'Til', 
            type: 'text',
            transform: (value: string) => {
              if (!value) return '-';
              if (value.length > 120) return value.substring(0, 120) + '…';
              return value;
            },
            width: '220px'
          }
          // Removed the actions (View Event) button column
        ],
        pageable: true,
        sortable: true,
        rowHover: true,
        searchable: true,
        defaultSortColumn: 'changed_at',
        defaultSortDirection: 'desc',
        facetFilters: AUDIT_FACET_FILTERS_EVENTS,
      }
    }
} 